import { useMemo } from 'react'
import { formatScore, getScoreColor } from 'react-common'
import {
  LearnerObjectiveAccessCompactModel,
  LearnerObjectiveAccessFullModel,
  ObjectiveWorkflowState,
} from '@/api/models/aggregate'
import { CourseResultBadge } from '@/components/CourseResultBadge/CourseResultBadge'
import { useGetCourseCoverColor } from '@/hooks/useGetCourseCoverColor'
import { useGetCourseStateTranslator } from '@/hooks/useGetCourseStateTranslator'
import { useIsCatalogCourse } from '@/hooks/useIsCatalogCourse'
import { CourseBottom } from './CourseBottom'
import styles from './CourseDetailsComponent.module.scss'
import { useGradingVisibility } from '../hooks/useGradingVisibility'

type Props = {
  aggregate: LearnerObjectiveAccessFullModel
}

export const CourseCover = ({ aggregate }: Props) => {
  const { objectiveWorkflowAggregate } = aggregate || {}
  const { id: aggregateId } = objectiveWorkflowAggregate || {}

  const objective = useMemo(() => aggregate.objective, [aggregate])

  const { grade } = aggregate.objectiveWorkflowAggregate?.lastObjectiveRecord || {}

  const { showGrade, isCompletedBeforeGradeReveal, gradeDotColor, gradingLabel, finishedScore } = useGradingVisibility(
    aggregate as LearnerObjectiveAccessCompactModel,
  )

  const badge = useMemo(() => {
    if (finishedScore) return finishedScore
    if (showGrade) return gradingLabel
    if (isCompletedBeforeGradeReveal) return gradingLabel

    return undefined
  }, [finishedScore, gradingLabel, isCompletedBeforeGradeReveal, showGrade])

  const isGradingInProgress = badge === gradingLabel

  const score = useMemo(() => {
    const { lastObjectiveRecord } = aggregate.objectiveWorkflowAggregate || {}
    return lastObjectiveRecord ? formatScore(lastObjectiveRecord) : '-'
  }, [aggregate])

  const dotBackgroundColor = useMemo(() => {
    const { lastObjectiveRecord } = aggregate.objectiveWorkflowAggregate || {}
    const { objectiveGradingScale } = aggregate.objective || {}

    const objectiveGradingScaleDefault = objectiveGradingScale || {
      id: '00000000-0000-0000-0000-000000000000',
      maxScore: 0,
      name: '',
      description: '',
      normalizedPassingScore: 0,
      numberOfGrades: 0,
      objectiveGrades: null,
      passingGradeId: '00000000-0000-0000-0000-000000000000',
    }

    return lastObjectiveRecord && objectiveGradingScaleDefault
      ? getScoreColor(lastObjectiveRecord, objectiveGradingScaleDefault)
      : undefined
  }, [aggregate])

  const dotStyles = useMemo(
    () => ({ backgroundColor: finishedScore ? dotBackgroundColor : gradeDotColor }),
    [finishedScore, gradeDotColor, dotBackgroundColor],
  )

  const getStateTranslatedString = useGetCourseStateTranslator()

  const { isPassed, showPassed, state } = useMemo(() => {
    const { lastObjectiveRecord, lastObjectiveWorkflow } = aggregate.objectiveWorkflowAggregate || {}

    const isPassed = lastObjectiveRecord?.passed

    const showPassed =
      lastObjectiveWorkflow?.state === ObjectiveWorkflowState.graded ||
      lastObjectiveWorkflow?.state === ObjectiveWorkflowState.finished ||
      isPassed

    const state = lastObjectiveWorkflow?.state
      ? getStateTranslatedString(lastObjectiveWorkflow.state)
      : isPassed
        ? getStateTranslatedString(ObjectiveWorkflowState.finished)
        : ''

    return { isPassed, showPassed, state }
  }, [aggregate, getStateTranslatedString])

  const isCatalogCourse = useIsCatalogCourse()
  const coverBackgroundColor = useGetCourseCoverColor(aggregateId!)

  return (
    <div className={styles.coverWrapper} style={{ backgroundColor: coverBackgroundColor }}>
      {score !== null && badge && (
        <CourseResultBadge
          grade={grade}
          label={badge}
          dotStyles={dotStyles}
          className={styles.resultBadge}
          isPassed={isPassed}
          state={state}
          score={score}
          showPassed={showPassed}
        />
      )}
      {isGradingInProgress && (
        <CourseResultBadge label={gradingLabel} dotStyles={dotStyles} className={styles.resultBadge} />
      )}
      {objective.imageUrl ? (
        <img src={objective.imageUrl} alt="Course cover" className={styles.cover} />
      ) : (
        <div className={styles.cover} />
      )}
      {!isCatalogCourse && <CourseBottom aggregate={aggregate} />}
    </div>
  )
}
